/**
 * API functions for Database Viewer feature
 */

import type {
  ModelsResponse,
  ModelDataResponse,
  ModelDataOptions,
  DatabaseApiError,
} from '@/types/database'

function getCsrfToken(): string {
  return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
}

function getAuthHeaders(): HeadersInit {
  return {
    'Accept': 'application/json',
    'X-CSRF-Token': getCsrfToken(),
  }
}

async function handleApiResponse<T>(response: Response, fallbackError: string): Promise<T> {
  if (!response.ok) {
    let errorMessage = fallbackError
    try {
      const data = await response.json() as DatabaseApiError
      errorMessage = data.error || errorMessage
    } catch {
      // Response wasn't JSON (e.g., 502 HTML page)
    }
    throw new Error(errorMessage)
  }
  return response.json() as Promise<T>
}

/**
 * Fetch all models for a project
 */
export async function fetchModels(projectId: number): Promise<ModelsResponse> {
  const response = await fetch(`/api/projects/${projectId}/database/models`, {
    method: 'GET',
    headers: getAuthHeaders(),
  })
  return handleApiResponse<ModelsResponse>(response, 'Failed to fetch models')
}

/**
 * Reload models by restarting the database worker (refreshes Django app registry).
 * Returns fresh models list after worker restart.
 */
export async function reloadModels(projectId: number): Promise<ModelsResponse> {
  const response = await fetch(`/api/projects/${projectId}/database/reload`, {
    method: 'POST',
    headers: {
      ...getAuthHeaders(),
      'Content-Type': 'application/json',
    },
  })
  return handleApiResponse<ModelsResponse>(response, 'Failed to reload models')
}

/**
 * Fetch data for a specific model with pagination and filtering
 */
export async function fetchModelData(
  projectId: number,
  appLabel: string,
  modelName: string,
  options: ModelDataOptions = {}
): Promise<ModelDataResponse> {
  const params = new URLSearchParams()
  if (options.page) params.set('page', String(options.page))
  if (options.page_size) params.set('page_size', String(options.page_size))
  if (options.order_by) params.set('order_by', options.order_by)
  if (options.search) params.set('search', options.search)

  const queryString = params.toString()
  const encodedAppLabel = encodeURIComponent(appLabel)
  const encodedModelName = encodeURIComponent(modelName)
  const url = `/api/projects/${projectId}/database/models/${encodedAppLabel}/${encodedModelName}/data${queryString ? `?${queryString}` : ''}`

  const response = await fetch(url, {
    method: 'GET',
    headers: getAuthHeaders(),
  })
  return handleApiResponse<ModelDataResponse>(response, 'Failed to fetch model data')
}
